// Minimal Commodore BASIC V2-ish interpreter.
// Supports: PRINT, LET, GOTO, GOSUB/RETURN, IF/THEN, FOR/TO/STEP/NEXT,
// INPUT (line-buffered), REM, END, LIST, RUN, NEW, SAVE/LOAD, CLS,
// arithmetic, string vars (A$), comparisons.

export type Output = string;

interface ForFrame {
  varName: string;
  end: number;
  step: number;
  returnLine: number; // line number to loop back to (line after FOR)
}

interface ProgramLine {
  num: number;
  src: string;
}

export class BasicInterpreter {
  private program: Map<number, string> = new Map();
  private vars: Map<string, number | string> = new Map();
  private callStack: number[] = []; // GOSUB return targets (line numbers)
  private forStack: ForFrame[] = [];
  private outputBuffer: Output[] = [];
  private pendingInput: { varName: string; resolve: () => void } | null = null;
  private inputResolver: ((value: string) => void) | null = null;
  private halted = false;
  private nextLineHint: number | null = null; // for GOTO/GOSUB during step
  private storageKey: string;

  constructor(storageKey = 'gb4k_basic_program_v1') {
    this.storageKey = storageKey;
  }

  // === Public API ===

  // Process a line entered at the immediate-mode prompt.
  // Returns the output lines accumulated during processing.
  async submitLine(line: string): Promise<Output[]> {
    this.outputBuffer = [];
    const trimmed = line.trim();
    if (trimmed === '') return [];

    // Numbered line: store in program
    const numMatch = trimmed.match(/^(\d+)\s*(.*)$/);
    if (numMatch) {
      const lineNum = parseInt(numMatch[1], 10);
      const body = numMatch[2];
      if (body === '') {
        this.program.delete(lineNum);
      } else {
        this.program.set(lineNum, body);
      }
      return this.outputBuffer;
    }

    // Immediate-mode statement
    await this.executeStatement(trimmed, -1);
    return this.outputBuffer;
  }

  // Resolve a pending INPUT with the user's response.
  resolveInput(value: string): void {
    if (this.inputResolver) {
      const r = this.inputResolver;
      this.inputResolver = null;
      r(value);
    }
  }

  isWaitingForInput(): boolean {
    return this.pendingInput !== null;
  }

  getInputVarName(): string | null {
    return this.pendingInput?.varName ?? null;
  }

  // === Internals ===

  private async executeStatement(src: string, lineNum: number): Promise<void> {
    // Multiple statements separated by ':' (BASIC convention)
    const statements = this.splitStatements(src);
    for (const stmt of statements) {
      if (this.halted) return;
      await this.executeOne(stmt.trim(), lineNum);
      if (this.nextLineHint !== null && lineNum >= 0) {
        // GOTO/GOSUB/RETURN/IF taken — don't run remaining stmts on this line
        return;
      }
    }
  }

  private splitStatements(src: string): string[] {
    // Split on ':' but not inside quoted strings
    const out: string[] = [];
    let current = '';
    let inQuote = false;
    for (let i = 0; i < src.length; i++) {
      const c = src[i];
      if (c === '"') inQuote = !inQuote;
      if (c === ':' && !inQuote) {
        out.push(current);
        current = '';
      } else {
        current += c;
      }
    }
    if (current) out.push(current);
    return out;
  }

  private async executeOne(stmt: string, lineNum: number): Promise<void> {
    if (stmt === '') return;
    const upper = stmt.toUpperCase();

    // ----- Commands -----
    if (upper === 'NEW') {
      this.program.clear();
      this.vars.clear();
      this.callStack = [];
      this.forStack = [];
      this.print('NEW PROGRAM.');
      return;
    }
    if (upper === 'LIST') {
      this.listProgram();
      return;
    }
    if (upper === 'RUN') {
      await this.runProgram();
      return;
    }
    if (upper === 'CLS' || upper === 'CLEAR') {
      this.outputBuffer.push('\x1bCLS'); // sentinel for the view layer
      return;
    }
    if (upper === 'END') {
      this.halted = true;
      return;
    }
    if (upper === 'SAVE') {
      this.saveProgram();
      this.print('PROGRAM SAVED.');
      return;
    }
    if (upper === 'LOAD') {
      const loaded = this.loadProgram();
      this.print(loaded ? 'PROGRAM LOADED.' : 'NO SAVED PROGRAM.');
      return;
    }
    if (upper.startsWith('REM')) {
      return; // comment
    }

    // PRINT
    if (upper.startsWith('PRINT') || stmt.startsWith('?')) {
      const arg = stmt.startsWith('?') ? stmt.substring(1) : stmt.substring(5);
      this.executePrint(arg.trim());
      return;
    }

    // GOTO
    const gotoMatch = upper.match(/^GOTO\s+(\d+)$/);
    if (gotoMatch) {
      const target = parseInt(gotoMatch[1], 10);
      if (!this.program.has(target)) {
        throw new Error(`UNDEFINED LINE ${target}`);
      }
      this.nextLineHint = target;
      return;
    }

    // GOSUB
    const gosubMatch = upper.match(/^GOSUB\s+(\d+)$/);
    if (gosubMatch) {
      const target = parseInt(gosubMatch[1], 10);
      if (!this.program.has(target)) {
        throw new Error(`UNDEFINED LINE ${target}`);
      }
      // Return target = the line after current
      const nextLine = this.findNextLineAfter(lineNum);
      this.callStack.push(nextLine ?? -1);
      this.nextLineHint = target;
      return;
    }

    // RETURN
    if (upper === 'RETURN') {
      if (this.callStack.length === 0) throw new Error('RETURN WITHOUT GOSUB');
      const ret = this.callStack.pop()!;
      if (ret === -1) {
        this.halted = true;
      } else {
        this.nextLineHint = ret;
      }
      return;
    }

    // IF ... THEN ...
    const ifMatch = stmt.match(/^IF\s+(.+?)\s+THEN\s+(.+)$/i);
    if (ifMatch) {
      const cond = this.evalExpr(ifMatch[1]);
      const condTruthy = typeof cond === 'number' ? cond !== 0 : cond.length > 0;
      if (condTruthy) {
        const action = ifMatch[2].trim();
        // "THEN <number>" is implicit GOTO
        if (/^\d+$/.test(action)) {
          this.nextLineHint = parseInt(action, 10);
        } else {
          await this.executeOne(action, lineNum);
        }
      }
      return;
    }

    // FOR var = a TO b [STEP c]
    const forMatch = stmt.match(/^FOR\s+(\w+)\s*=\s*(.+?)\s+TO\s+(.+?)(?:\s+STEP\s+(.+))?$/i);
    if (forMatch) {
      const varName = forMatch[1].toUpperCase();
      const start = this.toNumber(this.evalExpr(forMatch[2]));
      const end = this.toNumber(this.evalExpr(forMatch[3]));
      const step = forMatch[4] ? this.toNumber(this.evalExpr(forMatch[4])) : 1;
      this.vars.set(varName, start);
      const returnLine = this.findNextLineAfter(lineNum) ?? -1;
      this.forStack.push({ varName, end, step, returnLine });
      return;
    }

    // NEXT [var]
    const nextMatch = stmt.match(/^NEXT(?:\s+(\w+))?$/i);
    if (nextMatch) {
      if (this.forStack.length === 0) throw new Error('NEXT WITHOUT FOR');
      const frame = this.forStack[this.forStack.length - 1];
      const cur = this.toNumber(this.vars.get(frame.varName) ?? 0);
      const newVal = cur + frame.step;
      this.vars.set(frame.varName, newVal);
      const done = frame.step > 0 ? newVal > frame.end : newVal < frame.end;
      if (done) {
        this.forStack.pop();
      } else {
        this.nextLineHint = frame.returnLine;
      }
      return;
    }

    // INPUT
    const inputMatch = stmt.match(/^INPUT(?:\s+"([^"]*)";)?\s*(\w+\$?)$/i);
    if (inputMatch) {
      const prompt = inputMatch[1] ?? '?';
      const varName = inputMatch[2].toUpperCase();
      this.print(prompt + ' ', false);
      await this.waitForInput(varName);
      return;
    }

    // LET / direct assignment
    const letMatch = stmt.match(/^(?:LET\s+)?(\w+\$?)\s*=\s*(.+)$/i);
    if (letMatch) {
      const varName = letMatch[1].toUpperCase();
      const value = this.evalExpr(letMatch[2]);
      if (varName.endsWith('$') && typeof value !== 'string') {
        throw new Error('TYPE MISMATCH');
      }
      if (!varName.endsWith('$') && typeof value !== 'number') {
        throw new Error('TYPE MISMATCH');
      }
      this.vars.set(varName, value);
      return;
    }

    throw new Error(`SYNTAX ERROR: ${stmt}`);
  }

  private async waitForInput(varName: string): Promise<void> {
    this.pendingInput = { varName, resolve: () => {} };
    await new Promise<void>((resolve) => {
      this.inputResolver = (raw: string) => {
        // Try numeric coercion if varName isn't a string var
        let value: number | string;
        if (varName.endsWith('$')) {
          value = raw;
        } else {
          const n = parseFloat(raw);
          value = isNaN(n) ? 0 : n;
        }
        this.vars.set(varName, value);
        this.pendingInput = null;
        resolve();
      };
    });
  }

  private executePrint(arg: string): void {
    if (arg === '') {
      this.print('');
      return;
    }
    // PRINT a, b → tab-separated; PRINT a; b → concatenated; trailing ';' = no newline
    let result = '';
    let suppressNewline = false;

    // Split on top-level ',' and ';'
    const parts: { text: string; sep: ',' | ';' | '' }[] = [];
    let current = '';
    let inQuote = false;
    let depth = 0;
    for (let i = 0; i < arg.length; i++) {
      const c = arg[i];
      if (c === '"') inQuote = !inQuote;
      if (!inQuote) {
        if (c === '(') depth++;
        else if (c === ')') depth--;
      }
      if (!inQuote && depth === 0 && (c === ',' || c === ';')) {
        parts.push({ text: current.trim(), sep: c as ',' | ';' });
        current = '';
      } else {
        current += c;
      }
    }
    if (current.trim() !== '') {
      parts.push({ text: current.trim(), sep: '' });
    } else if (parts.length && parts[parts.length - 1].sep === ';') {
      suppressNewline = true;
    }

    for (const p of parts) {
      if (p.text !== '') {
        const v = this.evalExpr(p.text);
        result += typeof v === 'number' ? this.formatNumber(v) : v;
      }
      if (p.sep === ',') result += '\t';
    }
    this.print(result, !suppressNewline);
  }

  private formatNumber(n: number): string {
    if (Number.isInteger(n)) return String(n);
    return n.toString();
  }

  private listProgram(): void {
    const sorted: ProgramLine[] = Array.from(this.program.entries())
      .map(([num, src]) => ({ num, src }))
      .sort((a, b) => a.num - b.num);
    if (sorted.length === 0) {
      this.print('(empty)');
      return;
    }
    for (const l of sorted) {
      this.print(`${l.num} ${l.src}`);
    }
  }

  private async runProgram(): Promise<void> {
    this.callStack = [];
    this.forStack = [];
    this.halted = false;
    const lineNums = Array.from(this.program.keys()).sort((a, b) => a - b);
    if (lineNums.length === 0) {
      this.print('NO PROGRAM.');
      return;
    }
    let i = 0;
    let stepBudget = 50000; // safety: hard limit steps to prevent runaway
    while (i < lineNums.length && !this.halted) {
      if (--stepBudget <= 0) {
        this.print('?STOPPED: step limit reached.');
        break;
      }
      const ln = lineNums[i];
      const src = this.program.get(ln)!;
      this.nextLineHint = null;
      try {
        await this.executeStatement(src, ln);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        this.print(`?${msg} IN LINE ${ln}`);
        return;
      }
      if (this.nextLineHint !== null) {
        const target = this.nextLineHint;
        const idx = lineNums.indexOf(target);
        if (idx < 0) {
          this.print(`?UNDEFINED LINE ${target}`);
          return;
        }
        i = idx;
      } else {
        i++;
      }
    }
  }

  private findNextLineAfter(lineNum: number): number | null {
    if (lineNum < 0) return null;
    const lineNums = Array.from(this.program.keys()).sort((a, b) => a - b);
    const idx = lineNums.indexOf(lineNum);
    if (idx < 0 || idx + 1 >= lineNums.length) return null;
    return lineNums[idx + 1];
  }

  private saveProgram(): void {
    try {
      const dump = Array.from(this.program.entries()).map(([n, s]) => `${n} ${s}`).join('\n');
      localStorage.setItem(this.storageKey, dump);
    } catch {
      // ignore
    }
  }

  private loadProgram(): boolean {
    try {
      const dump = localStorage.getItem(this.storageKey);
      if (!dump) return false;
      this.program.clear();
      for (const line of dump.split('\n')) {
        const m = line.match(/^(\d+)\s*(.*)$/);
        if (m) this.program.set(parseInt(m[1], 10), m[2]);
      }
      return true;
    } catch {
      return false;
    }
  }

  // === Expression evaluator ===
  // Supports: numbers, strings, + - * / mod, parens, comparisons, var refs.
  private evalExpr(src: string): number | string {
    const tokens = this.tokenize(src);
    const parser = new ExprParser(tokens, this.vars);
    return parser.parseExpr();
  }

  private tokenize(src: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;
    while (i < src.length) {
      const c = src[i];
      if (/\s/.test(c)) { i++; continue; }
      if (c === '"') {
        let str = '';
        i++;
        while (i < src.length && src[i] !== '"') { str += src[i++]; }
        i++; // skip closing "
        tokens.push({ type: 'string', value: str });
      } else if (/\d/.test(c) || (c === '.' && /\d/.test(src[i + 1] || ''))) {
        let num = '';
        while (i < src.length && /[\d.]/.test(src[i])) { num += src[i++]; }
        tokens.push({ type: 'number', value: parseFloat(num) });
      } else if (/[A-Za-z]/.test(c)) {
        let id = '';
        while (i < src.length && /[A-Za-z0-9]/.test(src[i])) { id += src[i++]; }
        if (src[i] === '$') { id += '$'; i++; }
        tokens.push({ type: 'ident', value: id.toUpperCase() });
      } else if (c === '<' && src[i + 1] === '=') { tokens.push({ type: 'op', value: '<=' }); i += 2; }
      else if (c === '>' && src[i + 1] === '=') { tokens.push({ type: 'op', value: '>=' }); i += 2; }
      else if (c === '<' && src[i + 1] === '>') { tokens.push({ type: 'op', value: '<>' }); i += 2; }
      else if ('+-*/()=<>'.includes(c)) {
        tokens.push({ type: 'op', value: c });
        i++;
      } else {
        i++; // skip unknown
      }
    }
    return tokens;
  }

  private toNumber(v: number | string): number {
    if (typeof v === 'number') return v;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }

  private print(text: string, withNewline = true): void {
    if (withNewline) {
      this.outputBuffer.push(text);
    } else {
      // Append to last line if no newline
      if (this.outputBuffer.length === 0) {
        this.outputBuffer.push(text);
      } else {
        this.outputBuffer[this.outputBuffer.length - 1] += text;
      }
    }
  }
}

// === Expression parser ===
interface Token {
  type: 'number' | 'string' | 'ident' | 'op';
  value: any;
}

class ExprParser {
  private pos = 0;
  constructor(private tokens: Token[], private vars: Map<string, number | string>) {}

  parseExpr(): number | string {
    return this.parseComparison();
  }

  private parseComparison(): number | string {
    let left = this.parseAddSub();
    while (this.match('op', '=') || this.match('op', '<') || this.match('op', '>') ||
           this.match('op', '<=') || this.match('op', '>=') || this.match('op', '<>')) {
      const op = this.tokens[this.pos - 1].value;
      const right = this.parseAddSub();
      const ln = typeof left === 'number' ? left : 0;
      const rn = typeof right === 'number' ? right : 0;
      const ls = typeof left === 'string' ? left : String(left);
      const rs = typeof right === 'string' ? right : String(right);
      const eq = typeof left === 'string' || typeof right === 'string' ? ls === rs : ln === rn;
      const lt = typeof left === 'string' || typeof right === 'string' ? ls < rs : ln < rn;
      const gt = typeof left === 'string' || typeof right === 'string' ? ls > rs : ln > rn;
      let res = 0;
      if (op === '=') res = eq ? 1 : 0;
      else if (op === '<') res = lt ? 1 : 0;
      else if (op === '>') res = gt ? 1 : 0;
      else if (op === '<=') res = (lt || eq) ? 1 : 0;
      else if (op === '>=') res = (gt || eq) ? 1 : 0;
      else if (op === '<>') res = !eq ? 1 : 0;
      left = res;
    }
    return left;
  }

  private parseAddSub(): number | string {
    let left = this.parseMulDiv();
    while (this.match('op', '+') || this.match('op', '-')) {
      const op = this.tokens[this.pos - 1].value;
      const right = this.parseMulDiv();
      if (op === '+') {
        if (typeof left === 'string' || typeof right === 'string') {
          left = String(left) + String(right);
        } else {
          left = left + right;
        }
      } else {
        if (typeof left === 'string' || typeof right === 'string') throw new Error('TYPE MISMATCH');
        left = left - right;
      }
    }
    return left;
  }

  private parseMulDiv(): number | string {
    let left = this.parsePrimary();
    while (this.match('op', '*') || this.match('op', '/')) {
      const op = this.tokens[this.pos - 1].value;
      const right = this.parsePrimary();
      if (typeof left === 'string' || typeof right === 'string') throw new Error('TYPE MISMATCH');
      if (op === '*') left = left * right;
      else {
        if (right === 0) throw new Error('DIVISION BY ZERO');
        left = left / right;
      }
    }
    return left;
  }

  private parsePrimary(): number | string {
    const t = this.tokens[this.pos];
    if (!t) throw new Error('UNEXPECTED END OF EXPRESSION');
    if (t.type === 'number') { this.pos++; return t.value; }
    if (t.type === 'string') { this.pos++; return t.value; }
    if (t.type === 'op' && t.value === '-') {
      this.pos++;
      const v = this.parsePrimary();
      if (typeof v === 'string') throw new Error('TYPE MISMATCH');
      return -v;
    }
    if (t.type === 'op' && t.value === '(') {
      this.pos++;
      const v = this.parseExpr();
      if (!this.match('op', ')')) throw new Error('MISSING )');
      return v;
    }
    if (t.type === 'ident') {
      this.pos++;
      const name = t.value as string;
      // Built-in funcs
      if (this.match('op', '(')) {
        const arg = this.parseExpr();
        if (!this.match('op', ')')) throw new Error('MISSING )');
        return this.callFunc(name, arg);
      }
      const v = this.vars.get(name);
      if (v === undefined) return name.endsWith('$') ? '' : 0;
      return v;
    }
    throw new Error(`SYNTAX ERROR AT TOKEN ${t.value}`);
  }

  private callFunc(name: string, arg: number | string): number | string {
    switch (name) {
      case 'ABS': return Math.abs(this.num(arg));
      case 'INT': return Math.floor(this.num(arg));
      case 'SQR': return Math.sqrt(this.num(arg));
      case 'RND': return Math.random();
      case 'LEN': return typeof arg === 'string' ? arg.length : String(arg).length;
      case 'CHR$': return String.fromCharCode(this.num(arg));
      case 'ASC': return typeof arg === 'string' ? arg.charCodeAt(0) : 0;
      case 'STR$': return String(arg);
      case 'VAL': return parseFloat(String(arg)) || 0;
      case 'SIN': return Math.sin(this.num(arg));
      case 'COS': return Math.cos(this.num(arg));
      case 'TAN': return Math.tan(this.num(arg));
      default: throw new Error(`UNDEFINED FUNCTION ${name}`);
    }
  }

  private num(v: number | string): number {
    if (typeof v === 'number') return v;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }

  private match(type: string, value?: string): boolean {
    const t = this.tokens[this.pos];
    if (!t) return false;
    if (t.type !== type) return false;
    if (value !== undefined && t.value !== value) return false;
    this.pos++;
    return true;
  }
}
